import { LoadingButton } from "@mui/lab";
import {
  Button,
  Card,
  CardContent,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Box,
  Checkbox,
  ListItemText,
} from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";
import PostHooks from "../../../Api/PostHooks";
import { useLocation, useNavigate } from "react-router-dom";
import AlertComponent from "../../../Components/AlertComponent";
import Page from "../../../Components/Page";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Import Step Components
import Step1_BasicInfo from "./CreateProductFormSteps/Step1_BasicInfo";
import Step2_LoanAmounts from "./CreateProductFormSteps/Step2_LoanAmounts";
import Step3_InterestAndFees from "./CreateProductFormSteps/Step3_InterestAndFees";
import Step4_GuarantorsAndSavings from "./CreateProductFormSteps/Step4_GuarantorsAndSavings";
import Step5_RepaymentAndRecovery from "./CreateProductFormSteps/Step5_RepaymentAndRecovery";
import Step6_OtherConfig from "./CreateProductFormSteps/Step6_OtherConfig";
import Step7_Confirmation from "./CreateProductFormSteps/Step7_Confirmation";

const steps = [
  "Basic Info",
  "Loan Amounts",
  "Interest & Fees",
  "Guarantors & Savings",
  "Repayment & Recovery",
  "Other Configuration",
  "Confirmation",
];

const CreateProductForm = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [loading, response, request] = PostHooks();
  const [activeStep, setActiveStep] = useState(0);

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      loan_approval_date: new Date(),
      loan_start_date: new Date(),
      loan_end_date: new Date(),
      rollover_start_date: new Date(),
      active_status: "active",
      loan_approval: "engine",
      debt_collection_fee: "0",
      application_fee_amount: "0",
      agent_commission: "0",
      top_up_amount: "0",
      insurance_amount: "0",
      allow_multiplier_times: "NO",
      marker: "",
      checker: "",
    },
  });

  const { handleSubmit, trigger, watch } = methods;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Options
  const loanTypeOptions = [
    { value: "postpaid", label: "Postpaid" },
    { value: "prepaid", label: "Prepaid" },
  ];
  const currencyOptions = [
    { value: "KES", label: "KES" },
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
  ];
  const loanDurationOptions = [
    { value: "month", label: "Month" },
    { value: "two month", label: "Two Months" },
    { value: "three month", label: "Three Months" },
    { value: "six month", label: "Six Months" },
    { value: "year", label: "Year" },
    { value: "two year", label: "Two Years" },
    { value: "three year", label: "Three Years" },
    { value: "four year", label: "Four Years" },
    { value: "five year", label: "Five Years" },
  ];
  const guarantorshipOptions = [
    { value: "YES", label: "Other" },
    { value: "NO", label: "Self" },
  ];
  const yesNoOptions = [
    { value: "YES", label: "Yes" },
    { value: "NO", label: "No" },
  ];
  const interestTypeOptions = [
    { value: "reducing_balance", label: "Reducing Balance" },
    { value: "static", label: "Static" },
  ];
  const loanInterestFrequencyOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "two_months", label: "Two months" },
    { value: "three_months", label: "Three months" },
    { value: "six_months", label: "Six months" },
    { value: "one_year", label: "One year" },
  ];
  const upfrontOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  const earlyRepaymentPlanOptions = [
    { value: "days_in_loan", label: "Days in loan" },
    { value: "full_loan", label: "Full loan" },
  ];
  const notificationFreqOptions = [
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "two_weeks", label: "Two weeks" },
    { value: "monthly", label: "Monthly" },
    { value: "three_months", label: "Three Months" },
    { value: "six_months", label: "Six Months" },
    { value: "yearly", label: "Yearly" },
  ];
  const rolloverFrequencyOptions = [
    { value: "day", label: "Daily" },
    { value: "week", label: "weekly" },
    { value: "month", label: "Monthly" },
  ];
  const recoveryOptions = [
    { value: "debt_collection", label: "Debt collection" },
    { value: "rollover", label: "Rollover" },
    { value: "loan_end_date", label: "Loan end date" },
    { value: "before_loan_end_date", label: "Before Loan end date" },
  ];
  const debtCollectionTypeOptions = [
    { value: "one_off", label: "One off" },
    { value: "reccur", label: "Reccur" },
  ];
  const priorityRuleOptions = [
    { value: "principal", label: "principal" },
    { value: "application_fee", label: "application_fee" },
    { value: "other_charges", label: "other_charges" },
    { value: "insurance", label: "insurance" },
    { value: "interest", label: "interest" },
    { value: "rollover", label: "rollover" },
    { value: "debt_collection", label: "debt_collection" },
  ];

  const renderPriorityRuleOption = (option, selectedValue) => (
    <>
      <Checkbox checked={selectedValue.includes(option.value)} />
      <ListItemText primary={option.label} />
    </>
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data) => {
    console.log("data", data);

    try {
      const now = new Date();
      let formData = {
        ...data,
        entity_id: user.id,
        product_owner_id: user.id,
        publisher_name: user.name || "Onfon Ltd", // Fallback if user.name is not available
        loan_approval_date: moment(data.loan_approval_date).format(
          "YYYY-MM-DD hh:mm:ss"
        ),
        loan_start_date: moment(now).format("YYYY-MM-DD hh:mm:ss"),
        loan_end_date: moment(now).format("YYYY-MM-DD hh:mm:ss"),
        priority_rule: JSON.stringify(data.priority_rule || []),
        rollover_start_date: "",
      };

      let reqProps = {
        url: "create_product",
        body: formData,
      };

      const response = await request(reqProps);

      if (response && response.status === 200) {
        if (response && response.data.StatusCode === 0) {
          setSnackbar({
            open: true,
            severity: "success",
            message: "Product created successfully!",
          });

          setTimeout(() => {
            navigate("/dashboard/products");
          }, 500);
        } else if (response && response.data.StatusCode !== 0) {
          setSnackbar({
            open: true,
            severity: "error",
            message: response.data.StatusMessage,
          });
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.StatusMessage ??
        error.message ??
        "An unexpected error occurred. Please try again.";

      setSnackbar({
        open: true,
        severity: "error",
        message: errorMessage,
      });
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Step1_BasicInfo
            loanTypeOptions={loanTypeOptions}
            currencyOptions={currencyOptions}
            loanDurationOptions={loanDurationOptions}
          />
        );
      case 1:
        return <Step2_LoanAmounts />;
      case 2:
        return (
          <Step3_InterestAndFees
            interestTypeOptions={interestTypeOptions}
            loanInterestFrequencyOptions={loanInterestFrequencyOptions}
            upfrontOptions={upfrontOptions}
            yesNoOptions={yesNoOptions}
          />
        );
      case 3:
        return (
          <Step4_GuarantorsAndSavings
            guarantorshipOptions={guarantorshipOptions}
            yesNoOptions={yesNoOptions}
          />
        );
      case 4:
        return (
          <Step5_RepaymentAndRecovery
            earlyRepaymentOptions={yesNoOptions}
            earlyRepaymentPlanOptions={earlyRepaymentPlanOptions}
            debtCollectionTypeOptions={debtCollectionTypeOptions}
            recoveryOptions={recoveryOptions}
            rolloverFrequencyOptions={rolloverFrequencyOptions}
            yesNoOptions={yesNoOptions}
          />
        );
      case 5:
        return (
          <Step6_OtherConfig
            notificationFreqOptions={notificationFreqOptions}
            priorityRuleOptions={priorityRuleOptions}
            yesNoOptions={yesNoOptions}
            renderPriorityRuleOption={renderPriorityRuleOption}
          />
        );
      case 6:
        return <Step7_Confirmation setActiveStep={setActiveStep} />;
      default:
        return "Unknown step";
    }
  };

  return (
    <Page
      title={
        <Typography variant="h4" fontWeight="bold">
          Product creation form
        </Typography>
      }
      actions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      }
    >
      <Card>
        <CardContent>
          <AlertComponent
            open={snackbar.open}
            handleSnackbar={handleSnackbarClose}
            severity={{
              severity: snackbar.severity,
              message: snackbar.message,
            }}
          />

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mt: 2, mb: 4 }}>{getStepContent(activeStep)}</Box>

              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                  size="large"
                  variant="outlined"
                >
                  Back
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
                {activeStep === steps.length - 1 ? (
                  <LoadingButton
                    loading={loading}
                    variant="contained"
                    type="submit"
                    size="large"
                  >
                    Create Product
                  </LoadingButton>
                ) : (
                  <Button onClick={handleNext} variant="contained" size="large">
                    Next
                  </Button>
                )}
              </Box>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </Page>
  );
};

export default CreateProductForm;
